import React, { useState, useEffect, useMemo } from 'react';
import {
  Card, Button, Table, Modal, Form, Input, Select, Space, Tag,
  Badge, Row, Col, Statistic, Typography, Popconfirm, Alert,
  InputNumber, Tooltip,
} from 'antd';
import { staticApi } from '../utils/staticApi';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, CheckOutlined,
  ToolOutlined, ClockCircleOutlined, PauseCircleOutlined,
  ExclamationCircleOutlined, CalendarOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useCondoStore } from '../store/condoStore';
import type { MaintenanceSchedule, MaintenanceScheduleStatus } from '../types';

const { Text, Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const FREQUENCY_LABELS: Record<string, string> = {
  WEEKLY: 'Weekly', MONTHLY: 'Monthly', QUARTERLY: 'Quarterly',
  BIANNUAL: 'Every 6 Months', ANNUAL: 'Annual',
};

const FREQUENCY_COLORS: Record<string, string> = {
  WEEKLY: 'red', MONTHLY: 'blue', QUARTERLY: 'purple',
  BIANNUAL: 'cyan', ANNUAL: 'green',
};

const STATUS_CONFIG: Record<MaintenanceScheduleStatus, { badge: 'success' | 'warning' | 'error' | 'default'; label: string }> = {
  ACTIVE:       { badge: 'success', label: 'Active' },
  PAUSED:       { badge: 'warning', label: 'Paused' },
  DISCONTINUED: { badge: 'error',   label: 'Discontinued' },
};

const CATEGORIES = [
  'HVAC', 'PLUMBING', 'ELECTRICAL', 'FIRE_SAFETY', 'ELEVATOR',
  'LANDSCAPING', 'CLEANING', 'PEST_CONTROL', 'ROOF', 'POOL', 'OTHER',
];

const MaintenanceSchedulePage: React.FC = () => {
  const {
    maintenanceSchedules, fetchMaintenanceSchedules, createMaintenanceSchedule,
    updateMaintenanceSchedule, completeMaintenanceSchedule, setMaintenanceScheduleStatus,
    deleteMaintenanceSchedule, currentCondo, isLoading,
  } = useCondoStore() as any;

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<MaintenanceSchedule | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [form] = Form.useForm();

  useEffect(() => {
    if (currentCondo?.id) fetchMaintenanceSchedules(currentCondo.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- condo-scoped fetch-on-mount; fetchMaintenanceSchedules is a stable zustand action
  }, [currentCondo?.id]);

  const today = dayjs();

  const filtered = (maintenanceSchedules as MaintenanceSchedule[]).filter(ms =>
    statusFilter === 'all' || ms.status === statusFilter
  );

  const stats = useMemo(() => ({
    total:     maintenanceSchedules.length,
    active:    maintenanceSchedules.filter((ms: MaintenanceSchedule) => ms.status === 'ACTIVE').length,
    overdue:   maintenanceSchedules.filter((ms: MaintenanceSchedule) => ms.status === 'ACTIVE' && dayjs(ms.nextDueDate).isBefore(today, 'day')).length,
    dueThisMonth: maintenanceSchedules.filter((ms: MaintenanceSchedule) =>
      ms.status === 'ACTIVE' &&
      !dayjs(ms.nextDueDate).isBefore(today, 'day') &&
      dayjs(ms.nextDueDate).isBefore(today.endOf('month'), 'day')
    ).length,
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `today` is a per-render dayjs() snapshot; including it would rebuild the memo every render. Recompute is driven by the schedules list.
  }), [maintenanceSchedules]);

  const handleOpenCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ status: 'ACTIVE', frequency: 'MONTHLY', nextDueDate: today.add(1, 'month').format('YYYY-MM-DD') });
    setModalOpen(true);
  };

  const handleOpenEdit = (ms: MaintenanceSchedule) => {
    setEditing(ms);
    form.setFieldsValue({
      title: ms.title,
      description: ms.description,
      category: ms.category,
      frequency: ms.frequency,
      nextDueDate: ms.nextDueDate,
      assignedVendorName: ms.assignedVendorName,
      estimatedCost: ms.estimatedCost,
      status: ms.status,
      notes: ms.notes,
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = { ...values, condoId: currentCondo?.id };
      if (editing) {
        await updateMaintenanceSchedule(editing.id, payload);
        staticApi.message.success('Schedule updated');
      } else {
        await createMaintenanceSchedule(payload);
        staticApi.message.success('Schedule created');
      }
      setModalOpen(false);
    } catch { /* validation */ }
  };

  const handleComplete = async (id: string, title: string) => {
    await completeMaintenanceSchedule(id);
    staticApi.message.success(`"${title}" marked complete — next due date advanced`);
  };

  const handleSetStatus = async (id: string, status: string) => {
    await setMaintenanceScheduleStatus(id, status);
    staticApi.message.info(`Status updated to ${status}`);
  };

  const handleDelete = async (id: string) => {
    await deleteMaintenanceSchedule(id);
    staticApi.message.success('Schedule removed');
  };

  const getDueSeverity = (ms: MaintenanceSchedule): 'overdue' | 'soon' | 'ok' => {
    const due = dayjs(ms.nextDueDate);
    if (due.isBefore(today, 'day')) return 'overdue';
    if (due.diff(today, 'day') <= 14) return 'soon';
    return 'ok';
  };

  const columns: ColumnsType<MaintenanceSchedule> = [
    {
      title: 'Task',
      key: 'task',
      render: (_, r) => {
        const sev = getDueSeverity(r);
        return (
          <Space direction="vertical" size={2}>
            <Text strong>{r.title}</Text>
            <Space size={4}>
              <Tag color={FREQUENCY_COLORS[r.frequency] || 'default'}>{FREQUENCY_LABELS[r.frequency] || r.frequency}</Tag>
              <Tag>{r.category}</Tag>
            </Space>
            {sev === 'overdue' && <Tag color="red" icon={<ExclamationCircleOutlined />}>OVERDUE</Tag>}
            {sev === 'soon' && <Tag color="orange" icon={<ClockCircleOutlined />}>Due Soon</Tag>}
          </Space>
        );
      },
    },
    {
      title: 'Vendor',
      dataIndex: 'assignedVendorName',
      key: 'vendor',
      width: 150,
      render: (v: string) => v ? <Text>{v}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Next Due',
      dataIndex: 'nextDueDate',
      key: 'nextDueDate',
      width: 120,
      render: (d: string, r) => {
        const sev = getDueSeverity(r);
        return (
          <Text style={{ color: sev === 'overdue' ? '#ff4d4f' : sev === 'soon' ? '#fa8c16' : undefined }}>
            {dayjs(d).format('MMM D, YYYY')}
          </Text>
        );
      },
      sorter: (a, b) => dayjs(a.nextDueDate).unix() - dayjs(b.nextDueDate).unix(),
      defaultSortOrder: 'ascend',
    },
    {
      title: 'Last Done',
      dataIndex: 'lastCompletedDate',
      key: 'lastCompletedDate',
      width: 120,
      render: (d: string) => d ? dayjs(d).format('MMM D, YYYY') : <Text type="secondary">Never</Text>,
    },
    {
      title: 'Est. Cost',
      dataIndex: 'estimatedCost',
      key: 'estimatedCost',
      width: 100,
      render: (v: number) => v ? `$${Number(v).toLocaleString()}` : <Text type="secondary">—</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (s: MaintenanceScheduleStatus) => {
        const cfg = STATUS_CONFIG[s] || STATUS_CONFIG.ACTIVE;
        return <Badge status={cfg.badge} text={cfg.label} />;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, r) => (
        <Space wrap>
          {r.status === 'ACTIVE' && (
            <Tooltip title="Mark as completed today">
              <Button size="small" type="primary" icon={<CheckOutlined />} onClick={() => handleComplete(r.id, r.title)}>
                Done
              </Button>
            </Tooltip>
          )}
          <Button size="small" icon={<EditOutlined />} onClick={() => handleOpenEdit(r)} />
          {r.status === 'ACTIVE' && (
            <Tooltip title="Pause schedule">
              <Button size="small" icon={<PauseCircleOutlined />} onClick={() => handleSetStatus(r.id, 'PAUSED')} />
            </Tooltip>
          )}
          {r.status === 'PAUSED' && (
            <Tooltip title="Resume schedule">
              <Button size="small" type="dashed" icon={<CalendarOutlined />} onClick={() => handleSetStatus(r.id, 'ACTIVE')} />
            </Tooltip>
          )}
          <Popconfirm title="Delete this schedule?" onConfirm={() => handleDelete(r.id)} okText="Delete" okButtonProps={{ danger: true }}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            <CalendarOutlined style={{ marginRight: 10, color: '#1890ff' }} />
            Recurring Maintenance Schedules
          </Title>
          <Text type="secondary">Plan and track preventive maintenance tasks across the building</Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate}>
          Add Schedule
        </Button>
      </div>

      {/* KPIs */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card><Statistic title="Total Schedules" value={stats.total} prefix={<ToolOutlined />} /></Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card><Statistic title="Active" value={stats.active} valueStyle={{ color: '#52c41a' }} /></Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Overdue"
              value={stats.overdue}
              valueStyle={{ color: stats.overdue > 0 ? '#ff4d4f' : undefined }}
              prefix={<ExclamationCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Due This Month"
              value={stats.dueThisMonth}
              valueStyle={{ color: stats.dueThisMonth > 0 ? '#fa8c16' : undefined }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {stats.overdue > 0 && (
        <Alert
          type="error"
          showIcon
          message={`${stats.overdue} maintenance task${stats.overdue > 1 ? 's are' : ' is'} overdue`}
          description="Review and complete overdue tasks to maintain building safety and compliance."
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Filter */}
      <Space style={{ marginBottom: 16 }}>
        <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 160 }}>
          <Option value="all">All Statuses</Option>
          <Option value="ACTIVE">Active</Option>
          <Option value="PAUSED">Paused</Option>
          <Option value="DISCONTINUED">Discontinued</Option>
        </Select>
      </Space>

      {/* Table */}
      <Card>
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={{ pageSize: 15 }}
          expandable={{
            expandedRowRender: (r) => r.notes ? (
              <div style={{ padding: '4px 0' }}>
                <Text type="secondary">Notes: </Text><Text>{r.notes}</Text>
              </div>
            ) : null,
            rowExpandable: (r) => !!r.notes,
          }}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={editing ? 'Edit Schedule' : 'Add Recurring Maintenance Task'}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        okText={editing ? 'Save Changes' : 'Create Schedule'}
        width={560}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="title" label="Task Title" rules={[{ required: true }]}>
            <Input placeholder="e.g. HVAC Filter Replacement" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="category" label="Category" rules={[{ required: true }]}>
                <Select>
                  {CATEGORIES.map(c => <Option key={c} value={c}>{c.replace('_', ' ')}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="frequency" label="Frequency" rules={[{ required: true }]}>
                <Select>
                  {Object.entries(FREQUENCY_LABELS).map(([k, v]) => <Option key={k} value={k}>{v}</Option>)}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="nextDueDate" label="Next Due Date" rules={[{ required: true }]}>
                <Input type="date" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="estimatedCost" label="Est. Cost ($)">
                <InputNumber style={{ width: '100%' }} min={0} precision={2} placeholder="0.00" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="assignedVendorName" label="Assigned Vendor">
                <Input placeholder="Vendor name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="Status">
                <Select>
                  <Option value="ACTIVE">Active</Option>
                  <Option value="PAUSED">Paused</Option>
                  <Option value="DISCONTINUED">Discontinued</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label="Description">
            <TextArea rows={2} placeholder="Task details, scope of work…" />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <TextArea rows={2} placeholder="Any additional notes…" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MaintenanceSchedulePage;
